/**
 * @file eye-dropper-coordinator.ts
 * @description Handles the Eye Dropper tool logic for color picking from the canvas.
 */

import { g, layers, Tool } from '@hcie/core';
import { EventBus } from '@hcie/shared';
import { PickedColor, EyeDropperTool } from '../eyedropper';

// Step 1: Initialize the utility instance
const eyeDropperUtility = new EyeDropperTool();

/**
 * @function handleEyeDropper
 * @description Picks a color from the drawing canvas at the mouse event location.
 * @param {MouseEvent} event - The mouse event (click or move) triggering the pick.
 * @returns {void}
 */
export function handleEyeDropper(event: MouseEvent): void {
    // [CRITICAL_LOGIC_START]
    const currentToolId = g.current_tool?.id;
    if (currentToolId !== Tool.Eye_Dropper.id) {
        return;
    }
    
    // Only pick if mouse is down (buttons === 1)
    if (event.type === 'mousemove' && event.buttons !== 1) {
        return;
    }

    // STEP 2: Use Core Pixel Coordinates (Bug Fix)
    // g.pX and g.pY are already resolved and scaled by coordinate-utils.ts
    const internalX: number = Math.floor(g.pX);
    const internalY: number = Math.floor(g.pY);

    // STEP 3: Boundary Check against project dimensions
    const isOutOfBounds: boolean = 
        internalX < 0 || 
        internalY < 0 || 
        internalX >= g.image_width || 
        internalY >= g.image_height;

    if (isOutOfBounds) {
        return;
    }

    try {
        // STEP 4: Color Extraction from Canvas Stack
        // Filter layers that have a valid canvas element and are visible
        const canvasStack = layers
            .filter(layer => layer.visible && layer.canvas)
            .map(layer => layer.canvas as HTMLCanvasElement);
        
        if (canvasStack.length === 0) return;

        const pickedColorData: PickedColor = eyeDropperUtility.pickColor(canvasStack, internalX, internalY);
        const hexColor: string = pickedColorData.hex;
        
        // STEP 5: State Update (#1326 Sync)
        g.pen_color = hexColor;
        
        // Notify UI and synchronize other components
        window.dispatchEvent(new CustomEvent('colorChanged', { 
            detail: { color: hexColor, source: 'eye-dropper' } 
        }));
        
        EventBus.emit('UPDATE_PROPERTIES_PANEL', { type: 'color', value: hexColor });
        
        // Final debug trace for confirmation
        console.log(`[EyeDropper] Picked: ${hexColor} at [${internalX}, ${internalY}]`);
    } catch (error) {
        console.error('[EyeDropper] Error picking color:', error);
    }
    // [CRITICAL_LOGIC_END]
}
